import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { getUserInfo } from "../api/getCommands";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
} from "@mui/material";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import SettingsIcon from "@mui/icons-material/Settings";
import SettingsPopup from "./SettingsPopup";
import FeedbackPopup from "./FeedbackPopup";
import GoogleSignIn from "./GoogleSignIn";
import Notifications from "./Notifications";
import Footer from "./Footer";
import { useTranslation } from "react-i18next";

const navItems = [
  { labelKey: "nav.home", path: "/", color: "#2979FF" },
  { labelKey: "nav.artistBrackets", path: "/game/artist", color: "#e74c3c" },
  { labelKey: "nav.customGames", path: "/game/custom", color: "#00B8D4" },
  { labelKey: "nav.explore", path: "/explore", color: "#f39c12" },
  { labelKey: "nav.history", path: "/history", color: "#6C4675" },
  { labelKey: "Blog", path: "/blog", color: "#1abc9c" },
];

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { t } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    getUserInfo()
      .then((data) => { if (data?.user) setIsLoggedIn(true); })
      .catch(() => { });
  }, []);

  return (
    <Box id="layout-root-1" style={{ height: "100vh" }} sx={{
      height: "100vh !important",
      display: "flex",
      flexDirection: "column",
      background: "radial-gradient(circle at 22% 18%, rgba(115, 130, 255, 0.08), transparent 30%), radial-gradient(circle at 78% 12%, rgba(255, 191, 135, 0.08), transparent 28%), linear-gradient(150deg, #0c121f 0%, #0a1426 40%, #0f1a2f 100%)",
      color: "#f5f7fb",
    }}>
      <AppBar id="header" position="static" sx={{
        background: "linear-gradient(135deg, #0b1021, rgba(11, 16, 33, 0.85))",
        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        boxShadow: "0 12px 32px rgba(0, 0, 0, 0.45)",
      }}>
        <Toolbar>
          <MusicNoteIcon sx={{ mr: 1, color: "primary.main" }} />
          <Box
            sx={{ flexGrow: 0, mr: 4, cursor: "pointer" }}
            onClick={() => navigate("/")}
          >
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              Music Brackets
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "grey.500", lineHeight: 1, fontSize: "14px" }}
            >
              Powered by{" "}
              <Box
                component="a"
                href="https://www.musicrandomizer.com"
                target="_blank"
                sx={{
                  color: "grey.300",
                  fontWeight: 600,
                  textDecoration: "underline",
                }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                MusicRandomizer.com
              </Box>
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexGrow: 1 }}>
            {navItems.map((item) => (
              <Button
                key={item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  color: location.pathname === item.path ? item.color : "grey.400",
                  borderBottom:
                    location.pathname === item.path
                      ? `2px solid ${item.color}`
                      : "2px solid transparent",
                  borderRadius: 0,
                  "&:hover": { color: item.color },
                }}
              >
                {t(item.labelKey)}
              </Button>
            ))}
          </Box>
          {isLoggedIn && (
            <Button
              onClick={() => setFeedbackOpen(true)}
              sx={{ color: "grey.400" }}
            >
              {t("nav.feedback")}
            </Button>
          )}
          <Notifications />
          <GoogleSignIn />
          <Button
            onClick={() => setSettingsOpen(true)}
            sx={{ color: "grey.400", ml: 1 }}
            startIcon={<SettingsIcon />}
          >
            {t("nav.settings")}
          </Button>
        </Toolbar>
      </AppBar>
      <Container id="main-content" maxWidth="lg" sx={{ flex: 1, minHeight: 0, overflow: "auto", py: 4, pb: 10 }}>
        <Outlet />
      </Container>
      <SettingsPopup open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <FeedbackPopup open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
      <Footer />
    </Box>
  );
}
